/**
 * Enum de roles del sistema según la jerarquía organizacional
 *
 * JRS CENTRAL (ADMIN): Puede crear forms, países, partners y asociar país/partner/form.
 *                       Puede editar y eliminar todo.
 *
 * PAÍS (COUNTRY_ADMIN): Puede ver forms, ver sus partners y sus beneficiarios asociados.
 *
 * PARTNER (PARTNER_USER): Puede ver/responder forms y crear, ver, editar y eliminar sus beneficiarios.
 */
export enum UserRole {
  /** Administrador central de JRS - Acceso total */
  ADMIN = 'SUPER_ADMIN',

  /** Usuario de país - Puede ver forms, partners y beneficiarios de su país */
  COUNTRY_ADMIN = 'COUNTRY_ADMIN',

  /** Usuario de partner - Puede gestionar sus beneficiarios y responder forms */
  PARTNER_USER = 'PARTNER_USER',
}

/**
 * Permisos asociados a cada rol
 */
export interface RolePermissions {
  // Forms
  canCreateForms: boolean;
  canViewForms: boolean;
  canEditForms: boolean;
  canDeleteForms: boolean;
  canRespondForms: boolean;

  // Countries
  canCreateCountries: boolean;
  canViewCountries: boolean;
  canEditCountries: boolean;
  canDeleteCountries: boolean;

  // Partners
  canCreatePartners: boolean;
  canViewPartners: boolean;
  canEditPartners: boolean;
  canDeletePartners: boolean;

  // Beneficiaries
  canCreateBeneficiaries: boolean;
  canViewBeneficiaries: boolean;
  canEditBeneficiaries: boolean;
  canDeleteBeneficiaries: boolean;

  // Associations
  canAssociateCountryPartnerForm: boolean;
}

/**
 * Mapa de permisos por rol
 */
export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  [UserRole.ADMIN]: {
    canCreateForms: true,
    canViewForms: true,
    canEditForms: true,
    canDeleteForms: true,
    canRespondForms: false,

    canCreateCountries: true,
    canViewCountries: true,
    canEditCountries: true,
    canDeleteCountries: true,

    canCreatePartners: true,
    canViewPartners: true,
    canEditPartners: true,
    canDeletePartners: true,

    canCreateBeneficiaries: true,
    canViewBeneficiaries: true,
    canEditBeneficiaries: true,
    canDeleteBeneficiaries: true,

    canAssociateCountryPartnerForm: true,
  },

  [UserRole.COUNTRY_ADMIN]: {
    canCreateForms: false,
    canViewForms: true,
    canEditForms: false,
    canDeleteForms: false,
    canRespondForms: false,

    canCreateCountries: false,
    canViewCountries: false,
    canEditCountries: false,
    canDeleteCountries: false,

    canCreatePartners: false,
    canViewPartners: true, // Solo los de su país
    canEditPartners: false,
    canDeletePartners: false,

    canCreateBeneficiaries: false,
    canViewBeneficiaries: true, // Solo los de su país
    canEditBeneficiaries: false,
    canDeleteBeneficiaries: false,

    canAssociateCountryPartnerForm: false,
  },

  [UserRole.PARTNER_USER]: {
    canCreateForms: false,
    canViewForms: true,
    canEditForms: false,
    canDeleteForms: false,
    canRespondForms: true,

    canCreateCountries: false,
    canViewCountries: false,
    canEditCountries: false,
    canDeleteCountries: false,

    canCreatePartners: false,
    canViewPartners: false,
    canEditPartners: false,
    canDeletePartners: false,

    canCreateBeneficiaries: true, // Solo los suyos
    canViewBeneficiaries: true, // Solo los suyos
    canEditBeneficiaries: true, // Solo los suyos
    canDeleteBeneficiaries: true, // Solo los suyos

    canAssociateCountryPartnerForm: false,
  },
};

/**
 * Helper para obtener permisos de un rol
 */
export function getPermissionsForRole(role: UserRole): RolePermissions {
  return ROLE_PERMISSIONS[role];
}

/**
 * Helper para verificar si un rol tiene un permiso específico
 */
export function hasPermission(role: UserRole, permission: keyof RolePermissions): boolean {
  return ROLE_PERMISSIONS[role][permission];
}
